import React from 'react';
import PropTypes from 'prop-types';
import Visualization from 'Components/UI/Snapshot/Holdings/Visualization';
import PortfolioComparisonBreakdown from 'Components/UI/Snapshot/Holdings/PortfolioComparisonBreakdown';
import PortfolioQueryWidget from 'Components/UI/Snapshot/Holdings/PortfolioQueryWidget';
import RecommendedPortfolioSelect from 'Components/UI/Snapshot/Holdings/RecommendedPortfolioSelect';
import HoldingsAccountList from 'Components/UI/Snapshot/Holdings/HoldingsAccountList';
import { styled } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  PortfolioProvider,
  usePortfolioStateContext,
} from 'Components/UI/Portfolio/PortfolioContext';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { useHistory, useLocation } from 'react-router';

const StyledPortfolioQueryWidget = styled(PortfolioQueryWidget)({
  gridColumn: 'col / span 4',
  gridRow: 'top',
});

const StyledRecommendedPortfolioSelect = styled(RecommendedPortfolioSelect)({
  gridColumn: 'col 21 / span 4',
  gridRow: 'top',
});

const CurrentPortfolioChart = styled(Visualization)({
  gridRow: 'charts',
  gridColumn: 'col / span 6',
});

const CurrentHoldingsAccountList = styled(HoldingsAccountList)({
  gridColumn: 'col / span 12',
  gridRow: 'body',
  margin: 0,
});

const RecommendedPortfolioChart = styled(Visualization)({
  gridRow: 'charts',
  gridColumn: 'col 18 / span 6',
});

const RecommendedHoldingsAccountList = styled(HoldingsAccountList)({
  gridColumn: 'col 13 / span 12',
  gridRow: 'body',
  margin: 0,
});

const StyledBreakdown = styled(PortfolioComparisonBreakdown)({
  gridRow: 'charts',
  gridColumn: 'col 9 / span 8',
});

const PortfolioGrid = styled(({ className }) => {
  const history = useHistory();
  const { portfolioCollection } = usePortfolioStateContext();
  const [selectedPortfolioName, setSelectedPortfolioName] = React.useState(
    null,
  );

  React.useEffect(() => {
    if (portfolioCollection) {
      const nextSearch = new URLSearchParams(history.location.search);
      nextSearch.set(
        'portfolioSelfLink',
        portfolioCollection.link('self').url(),
      );

      history.push({
        search: nextSearch.toString(),
      });
    }

    if (portfolioCollection && !selectedPortfolioName) {
      const [portfolio] = portfolioCollection;
      setSelectedPortfolioName(
        portfolio
          ?.attr('recommended')
          .resources()
          .list()[0]
          ?.attr('name'),
      );
    }
    return () => {
      const nextSearch = new URLSearchParams(history.location.search);
      nextSearch.delete('portfolioSelfLink');
      history.push({
        search: nextSearch.toString(),
      });
    };
  }, [portfolioCollection]);

  return (
    <div className={className}>
      <StyledPortfolioQueryWidget />
      <StyledRecommendedPortfolioSelect
        setSelectedPortfolioName={setSelectedPortfolioName}
        selectedPortfolioName={selectedPortfolioName}
      />
      <RecommendedComparisonContent
        selectedPortfolioName={selectedPortfolioName}
      />
    </div>
  );
})(
  props => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(24, [col] 1fr)',
    gridTemplateRows: '[top] 32px [charts] 400px [body] auto',
    gridGap: props.theme.spacing(1.5),
    width: '100%',
    placeItems: 'stretch',
  }),
  { withTheme: true },
);

const EXCLUDED_COLUMNS = [
  'Holding Name',
  'Price Date',
  'Quantity',
  'Cost Basis',
  'Current Price',
  'GainPercent',
  'Gain',
];
function RecommendedComparisonContent({ selectedPortfolioName, ...rest }) {
  const { portfolioCollection, loading } = usePortfolioStateContext();
  const [portfolio] = portfolioCollection || [];
  const selectedPortfolio = portfolio
    ?.attr('recommended')
    .resources()
    .list()
    .find(r => r.attr('name') === selectedPortfolioName);

  return (
    <>
      <CurrentPortfolioChart
        portfolio={portfolio}
        loading={loading}
        {...rest}
      />
      <StyledBreakdown
        portfolio={portfolio}
        loading={loading}
        selectedPortfolioName={selectedPortfolioName}
        {...rest}
      />
      <RecommendedPortfolioChart
        loading={loading}
        portfolio={selectedPortfolio || null}
        {...rest}
      />
      <CurrentHoldingsAccountList
        excludedColumns={EXCLUDED_COLUMNS}
        portfolio={portfolio}
        hidePercentReturn
        {...rest}
      />
      <RecommendedHoldingsAccountList
        title="Facet Recommended Holdings"
        excludedColumns={[...EXCLUDED_COLUMNS, 'Edit', 'Enabled']}
        portfolio={selectedPortfolio}
        hideExpensesPaidTooltip
        hidePercentReturn
        {...rest}
      />
    </>
  );
}

RecommendedComparisonContent.propTypes = {
  selectedPortfolioName: PropTypes.string.isRequired,
  portfolio: PropTypes.object.isRequired,
};

function RecommendedComparison() {
  const { householdResource } = useSimpleHousehold();
  const search = new URLSearchParams(useLocation().search);
  const portfolioRel = householdResource
    ?.relationship('portfolio')
    .link('related');

  const searchSelf = search.get('portfolioSelfLink');
  if (searchSelf) {
    portfolioRel.attr('url', searchSelf);
  }

  return (
    <PortfolioProvider portfolioRelationship={portfolioRel}>
      <PortfolioGrid />
    </PortfolioProvider>
  );
}

export default RecommendedComparison;
